<script setup>
import { computed, ref, watch } from 'vue';

import { usePlatform } from '../composables/usePlatform';
import {
  HrCard, HrForm, HrSpace, HrStandardDialog,
} from '../primitives';
import HrIconActionBtn from './HrIconActionBtn.vue';

/**
 * Form dialog — a modal dialog with a title header, scrollable form body, and a two-section footer for actions.
 *
 * @status recommended
 * @group cards-dialogs
 * @useWhen A form needs to be presented in a focused modal with title, submit, and cancel actions
 * @useWhen A multi-pane dialog needs a back control beside the title, via `header-leading`
 * @avoidWhen The form should adapt to a bottom sheet on mobile — use HrAdaptiveFormDialog
 * @avoidWhen The user could reasonably want to back out — `blocking` strands them if the form cannot be satisfied
 * @related HrAdaptiveFormDialog
 * @a11y Backdrop click never dismisses, and Escape only does so with `escDismiss` — otherwise the
 * user must use the close button, or complete the form when `blocking` is set. A form whose work
 * would be lost should guard the close button with `beforeDismiss` rather than removing it; Escape
 * runs through the same guard.
 * @interfaceShape imperative
 */
defineOptions({ name: 'HrFormDialog' });

/**
 * @typedef {'sm' | 'md' | 'lg'} HrFormDialogSize
 */

const props = defineProps({
  /**
   * Dialog width preset.
   * @values sm, md, lg
   */
  size: {
    type: String,
    default: 'sm',
    validator: (v) => ['sm', 'md', 'lg'].includes(v),
  },
  /**
   * Dialog title shown in the header bar.
   * @example Edit profile
   */
  title: {
    type: String,
    required: true,
  },
  /** Shows a minimize button in the header. */
  minimize: {
    type: Boolean,
    default: false,
  },
  /** Shows a maximize button in the header (on non-mobile only). */
  maximize: {
    type: Boolean,
    default: false,
  },
  /**
   * Initial maximized (full-screen) state on non-mobile. Two-way bindable via
   * `v-model:maximized`, so callers can remember the chosen size across close-and-reopen.
   * Seeds the internal state on mount; the dialog then owns it and emits `update:maximized`
   * on change (it does not require the value to be fed back — dialogs are typically opened
   * via the dialog plugin, whose props are static after mount).
   */
  maximized: {
    type: Boolean,
    default: false,
  },
  /**
   * Prevents returning focus to the element that opened the dialog when it closes. Set when the
   * opener is a keyboard-activatable control (e.g. a clickable list row) so an Enter-to-submit
   * doesn't re-fire on the opener and reopen the dialog.
   */
  noRefocus: {
    type: Boolean,
    default: false,
  },
  /**
   * Removes the close button, leaving completing the form as the only way out (Esc and backdrop
   * dismissal are always off). Reserve it for flows that would leave the app unusable if abandoned.
   * Bind it to whether submission is currently possible, rather than passing a bare `true` — a
   * blocking dialog whose submit is disabled traps the user with no way out.
   */
  blocking: {
    type: Boolean,
    default: false,
  },
  /**
   * Called before the close button dismisses the dialog. Return false, or a promise resolving to
   * false, to keep it open; anything else lets the dismissal proceed.
   *
   * Use it for a form whose work would be lost, so the caller can ask first. Without it the only
   * way to intervene is `blocking` plus a hand-rolled close button, which leaves the dialog
   * claiming to have no way out while quietly having one. A guard that rejects reads as a refusal.
   */
  beforeDismiss: {
    type: Function,
    default: null,
  },
  /**
   * Lets Escape ask to dismiss, through the same guard as the close button. Off by default so a
   * form is not abandoned by a stray keypress; set it where the spec calls for Escape to back out.
   * Ignored while `blocking`, which exists to leave completing the form as the only way out.
   */
  escDismiss: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits({
  /** Emitted when the minimize button in the header is clicked. */
  minimize: null,
  /** Emitted when the close (×) button in the header is clicked. */
  close: null,
  /** Forwarded from the inner HrForm on submit. */
  submit: null,
  /**
   * Forwarded from the inner HrForm when submission is refused because a field failed validation.
   * Carries the first offending component. A tabbed dialog needs this: the invalid field may sit on
   * a panel the user cannot see, so nothing appears to happen when they press submit.
   */
  validationError: null,
  /** Emitted when the maximized state changes (supports `v-model:maximized`). */
  'update:maximized': null,
});

const formRef = ref(null);
const standardDialogRef = ref(null);
const isMaximized = ref(props.maximized);

// Keep in sync when a reactive caller drives the prop (a plugin dialog's props are static,
// so this simply never fires there).
watch(() => props.maximized, (value) => {
  isMaximized.value = value;
});

const isMobile = computed(() => usePlatform().is.mobile);
// Mobile dialogs are always full-screen, so maximizing only applies elsewhere.
const isFullScreen = computed(() => isMobile.value || isMaximized.value);

function toggleMaximize() {
  isMaximized.value = !isMaximized.value;
  emit('update:maximized', isMaximized.value);
}

function validate(shouldFocus) {
  return formRef.value?.validate(shouldFocus);
}

function submit() {
  return formRef.value?.submit();
}

/*
 * The guard is awaited before anything hides, which is the whole point: emitting first and hiding
 * anyway would tell the caller what happened without letting it intervene.
 */
/*
 * One dismissal at a time. The guard is awaited, so without this a second click while it is still
 * deciding would run it again and, if both allowed, emit close twice for one dialog.
 */
const isDismissing = ref(false);

/*
 * A guard that failed has not agreed to the dismissal, so a rejection is read as a refusal: closing
 * on it would lose the very edits the guard exists to protect. Logged rather than swallowed, and
 * caught rather than left to propagate, because a rejection out of a click handler surfaces nowhere
 * as an error the user can act on while still counting as unhandled.
 */
async function isDismissalAllowed() {
  if (props.beforeDismiss === null) {
    return true;
  }

  try {
    return await props.beforeDismiss() !== false;
  } catch (error) {
    console.error('Dialog dismissal guard failed:', error);
    return false;
  }
}

async function requestDismiss() {
  if (isDismissing.value) {
    return;
  }
  isDismissing.value = true;

  try {
    if (!await isDismissalAllowed()) {
      return;
    }

    standardDialogRef.value?.hide();
    emit('close');
  } finally {
    isDismissing.value = false;
  }
}

defineExpose({
  /** Validates the form; returns a promise resolving to true if valid. */
  validate,
  /** Programmatically submits the form, triggering validation first. */
  submit,
  /** Opens the dialog. */
  show: () => standardDialogRef.value?.show(),
  /** Closes the dialog. */
  hide: () => standardDialogRef.value?.hide(),
});
</script>

<template>
  <hr-standard-dialog
    ref="standardDialogRef"
    :size="size"
    :maximized="isMaximized"
    :no-refocus="noRefocus"
    :esc-dismiss="escDismiss && !blocking"
    no-backdrop-dismiss
    no-route-dismiss
    data-e2e="dialog"
    @escape-key="requestDismiss"
  >
    <hr-card
      :shape="isFullScreen ? 'none' : 'xl'"
      variant="elevated"
      :class="['hr-form-dialog', {
        'hr-form-dialog--mobile': isMobile,
        'hr-form-dialog--desktop': !isFullScreen,
        'hr-form-dialog--maximized': !isMobile && isMaximized,
      }]"
    >
      <div
        class="row hr-form-dialog__header"
        data-e2e="dialog-header"
      >
        <div
          v-if="$slots['header-leading']"
          class="hr-form-dialog__header-leading"
        >
          <!-- @slot header-leading Content before the title, e.g. a back button on a multi-pane dialog. -->
          <slot name="header-leading" />
        </div>
        <span class="hr-headline-small hr-form-dialog__title">{{ title }}</span>
        <div class="hr-form-dialog__title-attributes">
          <!-- @slot title-attributes Chips stating the record's type or state, beside the title.
               Anything the user does to the record belongs in `header-actions` or the footer. -->
          <slot name="title-attributes" />
        </div>
        <hr-space />
        <div class="hr-form-dialog__header-actions">
          <!-- @slot header-actions Extra buttons in the header, left of the minimize/close group. -->
          <slot name="header-actions" />
          <hr-icon-action-btn
            v-if="minimize"
            icon="minimize"
            @click="emit('minimize')"
          />
          <hr-icon-action-btn
            v-if="maximize && !isMobile"
            :icon="isMaximized ? 'close_fullscreen' : 'open_in_full'"
            @click="toggleMaximize"
          />
          <hr-icon-action-btn
            v-if="!blocking"
            icon="close"
            @click="requestDismiss"
          />
        </div>
      </div>

      <hr-form
        ref="formRef"
        class="hr-form-dialog__body"
        @submit="emit('submit', $event)"
        @validation-error="emit('validationError', $event)"
      >
        <div class="hr-form-dialog__content">
          <!-- @slot Form fields and content. -->
          <slot />
        </div>

        <div class="hr-form-dialog__footer">
          <div class="hr-form-dialog__footer-start">
            <!-- @slot destructive-actions Footer left — destructive action buttons (e.g. delete). -->
            <slot name="destructive-actions" />
          </div>
          <div class="hr-form-dialog__footer-end">
            <!-- @slot actions Footer right — primary and secondary action buttons. -->
            <slot name="actions" />
          </div>
        </div>
      </hr-form>
    </hr-card>
  </hr-standard-dialog>
</template>

<style lang="scss" scoped>
.hr-form-dialog {
  display: flex;
  flex-direction: column;
  // The card is a tone above the page, so a search field on it sits a tone higher again.
  --hr-search-bar-container: var(--app-surface-container-highest);
}

.hr-form-dialog--mobile {
  height: 100%;

  .hr-form-dialog__header {
    padding-left: var(--app-comp-page-padding-sm);
  }

  /* Wrap the title so the buttons stay pinned to the top. */
  .hr-form-dialog__title {
    white-space: normal;
    overflow-wrap: anywhere;
  }

  .hr-form-dialog__header-actions {
    align-self: flex-start;
  }

  .hr-form-dialog__content {
    padding-inline: var(--app-comp-page-padding-sm);
  }
}

.hr-form-dialog--desktop {
  max-height: 90vh;
}

.hr-form-dialog--maximized {
  height: 100%;
}

.hr-form-dialog__title {
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

/*
 * The title yields and the attributes hold their width: a title is unbounded (a plan can be named
 * after a clinic's full address) while an attribute chip is short and bounded, so truncating the
 * one that can afford it keeps the other readable.
 *
 * The nudge is optical, not arbitrary. `align-items: center` centres on the title's LINE BOX, but
 * the eye reads alignment against the letters: headline-small is 24px on a 32px line, so 4px of
 * half-leading sits above the ascender, putting the cap-height centre roughly 2px below the box
 * centre. Without this the chip reads as pinned to the top edge.
 */
.hr-form-dialog__title-attributes {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-inline-start: 12px;
  margin-top: 2px;
}

/* Overrides the `row` class's wrap, which would drop the actions below a long title. */
.hr-form-dialog__header {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: space-between;
  padding: 16px 8px 16px 24px;
  flex-shrink: 0;
}

.hr-form-dialog__header-leading {
  display: flex;
  align-items: center;
  margin-inline-end: var(--app-comp-form-dialog-header-gap);
}

/*
 * Spaced with gap rather than q-col-gutter, which compensates its own negative margin with padding
 * on each child. A button sets its own padding and so drops that compensation, leaving anything
 * placed in the slot sitting a gutter above the close button.
 */
.hr-form-dialog__header-actions {
  display: flex;
  align-items: center;
  gap: var(--app-comp-form-dialog-header-gap);
}

.hr-form-dialog__body {
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
}

.hr-form-dialog__content {
  flex: 1;
  overflow-y: auto;
  padding: 0 24px 16px;
}

.hr-form-dialog__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  flex-shrink: 0;
}

.hr-form-dialog__footer-start,
.hr-form-dialog__footer-end {
  display: flex;
  align-items: center;
  gap: 8px;
}
</style>
